import { useEffect } from 'react'

import { fetchPropertyCalendarPrices } from 'actions/PropertyActions'
import { initialOfferPageRooms } from 'contexts/OfferPage/offerPageStateReducer'
import { EmptyObject } from 'lib/object/objectUtils'
import buildPropertyCalendarPricesKey from 'lib/offer/buildPropertyCalendarPricesKey'

import { useAppDispatch, useAppSelector } from './reduxHooks'

interface PropertyCalendarPricesHookParams {
  propertyId: string
  duration?: number
  occupancies?: Array<App.Occupants>
  disabled?: boolean
}

function usePropertyCalendarPrices({
  propertyId,
  duration,
  occupancies,
  disabled,
}: PropertyCalendarPricesHookParams): [{ [isoDate: string]: App.Property.CalendarDay }, boolean, string | undefined] {
  const dispatch = useAppDispatch()

  const property = useAppSelector(state => state.offer.properties[propertyId]?.property)
  const resolvedDuration = duration ?? property?.promotion?.marketedLos
  const resolvedOccupancies = occupancies ?? (property?.promotion?.marketedOccupancy ? [property.promotion.marketedOccupancy] : initialOfferPageRooms)
  const key = buildPropertyCalendarPricesKey({
    propertyId,
    duration: resolvedDuration,
    occupancies: resolvedOccupancies,
  })
  const record = useAppSelector(state => state.offer.propertyCalendarPrices[key])

  const isFetchRequired = !!key && !disabled && !record
  useEffect(() => {
    if (isFetchRequired) {
      dispatch(fetchPropertyCalendarPrices(propertyId, {
        duration: resolvedDuration!,
        occupancies: resolvedOccupancies,
      }))
    }
    // NB: `key` effectively (validates and) incorporates `propertyId`, `resolvedDuration`, and
    // `resolvedOccupancies`.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isFetchRequired, key])

  if (disabled) return [EmptyObject, false, undefined]
  return [
    record?.days ?? EmptyObject,
    record?.fetching ?? isFetchRequired,
    record?.error,
  ]
}

export default usePropertyCalendarPrices
